// Section-group colors for listing rows, Compare's rules (Compare.js
// listingGroupColors / getListingColor / getGroupName) as pure helpers: the
// same palette as the seat map and the zone list. Tested in
// groupColors.test.js.

// Skybox group colors arrive in mixed formats (int, "0x…", "rrggbb", "#rrggbb")
export const normalizeGroupColor = (rawColor) => {
  if (rawColor == null) return null;
  if (typeof rawColor === 'number') return '#' + rawColor.toString(16).padStart(6, '0');
  if (typeof rawColor === 'string' && rawColor) {
    if (rawColor.startsWith('0x')) return '#' + rawColor.slice(2);
    if (!rawColor.startsWith('#')) return '#' + rawColor;
    return rawColor;
  }
  return null;
};

// "#rrggbb" → rgba() at the given alpha (tints our rows per group)
export const hexToRgba = (hex, alpha) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * A listing's group color, looked up as Compare does: the listing's Skybox
 * groupId against the pricing payload's sectionGroups first (our inventory
 * carries one), then its sectionId through the seat chart — chart section →
 * chart group name → the sectionGroups entry of that name, case-insensitive
 * (market listings carry no groupId, and chart group ids are not Skybox ids).
 * Build once per (sectionGroups, chartData); the returned function gives a
 * "#rrggbb"-style color or null.
 * @param {{id, name, color}[]} sectionGroups  pricing payload's sectionGroups
 * @param {object} [chartData]                   buildChartData() output
 * @returns {(listing: object) => string|null}
 */
export function buildListingColorLookup(sectionGroups, chartData) {
  const byGroupId = new Map();
  const byName = new Map();
  for (const g of (sectionGroups || [])) {
    const color = normalizeGroupColor(g?.color);
    if (!color) continue;
    if (g.id != null) byGroupId.set(String(g.id), color);
    if (g.name) byName.set(g.name.toLowerCase(), color);
  }
  const bySectionId = new Map();
  if (chartData?.sections && chartData?.groups) {
    for (const [sectionId, info] of Object.entries(chartData.sections)) {
      const groupName = chartData.groups[info?.groupId];
      const color = groupName ? byName.get(groupName.toLowerCase()) : null;
      if (color) bySectionId.set(String(sectionId), color);
    }
  }
  return (listing) => {
    if (listing?.groupId != null) {
      const c = byGroupId.get(String(listing.groupId));
      if (c) return c;
    }
    if (listing?.sectionId) {
      const c = bySectionId.get(String(listing.sectionId));
      if (c) return c;
    }
    return null;
  };
}

// The name of the listing's Skybox group (its groupId in sectionGroups), or
// null — the title of a row's color dot
export function groupNameFor(sectionGroups, listing) {
  if (listing?.groupId == null) return null;
  const g = (sectionGroups || []).find(x => x && String(x.id) === String(listing.groupId));
  return g?.name || null;
}
